import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/AuthLayout";
import { TicketThread, TICKET_STATUS } from "@/components/support/TicketThread";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ subject: "", message: "" });
  const [text, setText] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/support/tickets");
    setTickets(data);
    setSel((s) => (s ? data.find((t) => t.id === s.id) || null : null));
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  useEffect(() => {
    if (sel && sel.user_seen === false) {
      api.post(`/support/tickets/${sel.id}/seen`).then(() => { window.dispatchEvent(new Event("jp-unread")); load(); });
    }
  }, [sel, load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/support/tickets", f);
      toast.success("Müraciətiniz göndərildi");
      setF({ subject: "", message: "" }); setCreating(false); await load(); setSel(data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const reply = async () => {
    try { await api.post(`/support/tickets/${sel.id}/messages`, { message: text }); setText(""); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12" data-testid="support-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-[#D4AF37] flex items-center gap-2"><LifeBuoy size={16} /> 24/7</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Texniki dəstək</h1>
        </div>
        <button onClick={() => { setCreating(true); setSel(null); }} className="btn-gold rounded-full px-6 py-3 font-display font-bold flex items-center gap-2" data-testid="new-ticket-btn"><Plus size={16} /> Yeni müraciət</button>
      </div>
      <div className="grid lg:grid-cols-[340px_1fr] gap-5">
        <div className="rounded-2xl border border-white/10 bg-[#111] divide-y divide-white/5 h-fit" data-testid="ticket-list">
          {tickets.length === 0 && <p className="p-5 text-sm text-zinc-500">Hələ müraciətiniz yoxdur</p>}
          {tickets.map((t) => (
            <button key={t.id} onClick={() => { setSel(t); setCreating(false); }} className={`w-full text-left px-4 py-3 transition-colors hover:bg-white/5 ${sel?.id === t.id ? "bg-white/5" : ""}`} data-testid={`ticket-${t.id}`}>
              <div className="flex justify-between gap-2">
                <p className="font-semibold truncate">{t.subject}</p>
                <span className={`text-[10px] rounded-full px-2 py-0.5 whitespace-nowrap ${TICKET_STATUS[t.status][1]}`}>{TICKET_STATUS[t.status][0]}</span>
              </div>
              <p className="text-xs text-zinc-500">{new Date(t.updated_at).toLocaleString("az-AZ")}</p>
            </button>
          ))}
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#111] p-6">
          {creating || !sel ? (
            <form noValidate onSubmit={create} className="space-y-5" data-testid="ticket-form">
              <h3 className="font-display font-bold text-lg">Probleminizi yazın</h3>
              <Field label="Mövzu" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Məs: Depozit balansa düşmədi" data-testid="ticket-subject-input" />
              <label className="block space-y-2">
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Mesaj</span>
                <textarea className="jp-input min-h-[140px]" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ticket-message-input" />
              </label>
              <button disabled={f.subject.trim().length < 3 || f.message.trim().length < 3} className="btn-gold rounded-full px-8 py-3 font-display font-bold" data-testid="ticket-submit-btn">Göndər</button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="font-display font-bold text-lg">{sel.subject}</p>
              <TicketThread ticket={sel} me="user" />
              {sel.status !== "closed" ? (
                <>
                  <textarea className="jp-input min-h-[80px]" placeholder="Mesaj yazın..." value={text} onChange={(e) => setText(e.target.value)} data-testid="ticket-reply-input" />
                  <button disabled={!text.trim()} onClick={reply} className="btn-gold rounded-full px-8 py-3 font-display font-bold" data-testid="ticket-reply-btn">Göndər</button>
                </>
              ) : <p className="text-sm text-zinc-500">Bu müraciət bağlanıb.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
